import React, { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, Clock, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND, buildWhatsappLink } from "@/lib/brand";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const maskPhone = (v) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(\d{4})(\d)/, "$1-$2");

export default function SchedulingSection() {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [booked, setBooked] = useState([]);
  const [loading, setLoading] = useState(false);

  const dateStr = date ? format(date, "yyyy-MM-dd") : "";

  const handleDateSelect = (d) => {
    setDate(d);
    setTime("");
    if (!d) {
      setBooked([]);
      return;
    }
    const ds = format(d, "yyyy-MM-dd");
    api
      .get(`/schedules/booked?date=${ds}`)
      .then(({ data }) => setBooked(data.booked || []))
      .catch(() => setBooked([]));
  };

  const submit = async () => {
    if (!name.trim() || phone.length < 14 || !date || !time) {
      toast.error("Preencha nome, telefone, data e horário.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/schedules", {
        name: name.trim(),
        phone,
        date: dateStr,
        time,
        solution: null,
      });
      const prettyDate = format(date, "dd/MM/yyyy", { locale: ptBR });
      const msg = `Olá EFORY! Quero confirmar meu agendamento.%0A%0ANome: ${name}%0AData: ${prettyDate}%0AHorário: ${time}`;
      toast.success("Agendamento registrado! Redirecionando para o WhatsApp...");
      window.open(buildWhatsappLink(decodeURIComponent(msg)), "_blank");
      setBooked((b) => [...b, time]);
      setTime("");
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        toast.error("Esse horário já foi reservado. Escolha outro.");
        setBooked((b) => [...b, time]);
        setTime("");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao agendar.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="agendamento" className="scroll-mt-nav bg-efory-ink text-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-efory-blue/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-efory-blue">
            <CalendarDays className="h-3.5 w-3.5" /> Agendamento
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-6 leading-[1.1]">
            Agende sua <span className="text-efory-blue">consultoria particular</span>
          </h2>
          <p className="mt-5 text-white/65 text-base sm:text-lg">
            Escolha o melhor dia e horário. Em seguida você será direcionado ao WhatsApp para
            confirmar o atendimento.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-8 items-start">
          {/* Calendar + times */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8"
          >
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                locale={ptBR}
                disabled={(d) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return d < today || d.getDay() === 0;
                }}
                data-testid="scheduling-calendar"
                className="rounded-xl text-white"
                classNames={{
                  caption_label: "text-sm font-medium text-white",
                  head_cell: "text-white/40 rounded-md w-9 font-normal text-[0.8rem]",
                  nav_button:
                    "h-7 w-7 bg-transparent p-0 text-white border border-white/20 rounded-md inline-flex items-center justify-center hover:bg-white/10 opacity-80 hover:opacity-100",
                  day_selected:
                    "bg-efory-blue text-white hover:bg-efory-blue hover:text-white focus:bg-efory-blue focus:text-white",
                  day_today: "bg-white/10 text-white",
                  day_outside: "text-white/30 opacity-50",
                  day_disabled: "text-white/20 opacity-50",
                }}
              />
            </div>

            <div className="mt-6">
              <Label className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <Clock className="h-4 w-4 text-efory-blue" /> Horários disponíveis
              </Label>
              {!date ? (
                <p className="text-sm text-white/40 mt-3">Selecione uma data para ver os horários.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2.5 mt-3">
                  {TIME_SLOTS.map((t) => {
                    const isBooked = booked.includes(t);
                    const active = time === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        disabled={isBooked}
                        onClick={() => setTime(t)}
                        data-testid={`time-slot-${t}`}
                        className={`h-10 rounded-lg text-sm font-semibold border transition-all duration-200 ${
                          isBooked
                            ? "bg-white/5 text-white/25 border-white/10 cursor-not-allowed line-through"
                            : active
                            ? "bg-efory-blue text-white border-efory-blue scale-105"
                            : "bg-white/5 text-white/80 border-white/15 hover:border-efory-blue hover:text-efory-blue"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl bg-white/[0.03] border border-white/10 text-white p-7 md:p-9"
          >
            <h3 className="font-heading text-2xl font-bold">Seus dados</h3>
            <p className="text-white/55 text-sm mt-1.5">Preencha para concluir o agendamento.</p>

            <div className="mt-6 space-y-5">
              <div>
                <Label htmlFor="sch-name" className="text-white/80 text-sm">Nome completo</Label>
                <Input
                  id="sch-name"
                  data-testid="scheduling-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                  className="mt-2 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-efory-blue focus-visible:border-efory-blue"
                />
              </div>
              <div>
                <Label htmlFor="sch-phone" className="text-white/80 text-sm">Telefone / WhatsApp</Label>
                <Input
                  id="sch-phone"
                  data-testid="scheduling-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(maskPhone(e.target.value))}
                  inputMode="numeric"
                  placeholder="(31) 99999-9999"
                  className="mt-2 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-efory-blue focus-visible:border-efory-blue"
                />
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                <div className="flex justify-between py-1">
                  <span className="text-white/55">Data</span>
                  <span className="font-semibold" data-testid="summary-date">
                    {date ? format(date, "dd/MM/yyyy", { locale: ptBR }) : "—"}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-white/55">Horário</span>
                  <span className="font-semibold" data-testid="summary-time">{time || "—"}</span>
                </div>
              </div>

              <Button
                onClick={submit}
                disabled={loading}
                data-testid="scheduling-submit-button"
                className="w-full h-12 bg-[#25D366] hover:bg-[#1ebe5b] text-white rounded-full font-semibold text-base transition-all duration-300 hover:scale-[1.02]"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <MessageCircle className="h-5 w-5 mr-2" /> Agendar e confirmar no WhatsApp
                  </>
                )}
              </Button>
              <p className="text-center text-xs text-white/45">
                Você será direcionado ao WhatsApp {BRAND.whatsappDisplay} para confirmação.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
